if (!customElements.get('product-thumbnails')) {
  customElements.define(
    'product-thumbnails',
    class ProductThumbnails extends HTMLElement {
      connectedCallback() {
        this.list = this.querySelector('[data-thumbs-list]') || this.querySelector('[id^="Slider-Thumbnails"]');
        this.prev = this.querySelector('button[name="previous"], [data-role="prev"]');
        this.next = this.querySelector('button[name="next"], [data-role="next"]');
        this.stepDesktop = Number(this.dataset.stepDesktop || 4);
        this.stepMobile = Number(this.dataset.stepMobile || 3);
        this.mql = window.matchMedia('(min-width: 750px)');

        // RAF throttle flag
        this._ticking = false;
        this._itemOffset = null;

        this.onScroll = () => this.requestUpdate();
        this.onResize = () => {
          this._itemOffset = null; // invalidate cache
          this.requestUpdate();
        };
        this.onPrev = this.scrollPrev.bind(this);
        this.onNext = this.scrollNext.bind(this);

        // Passive scroll for better perf
        this.list?.addEventListener('scroll', this.onScroll, { passive: true });
        this.prev?.addEventListener('click', this.onPrev);
        this.next?.addEventListener('click', this.onNext);
        this.mql.addEventListener('change', this.onResize);

        // Observe size/content changes and recalc lazily
        this.resizeObserver = new ResizeObserver(() => {
          this._itemOffset = null;
          this.requestUpdate();
        });
        this.list && this.resizeObserver.observe(this.list);

        // Update on image load (thumbnails often lazy-load)
        this.images = this.list ? this.list.querySelectorAll('img') : [];
        this.images && this.images.forEach((img) => img.addEventListener('load', this.onResize, { once: true }));

        // Initial state + delayed rechecks after paint
        queueMicrotask(() => this.requestUpdate());
        setTimeout(() => this.requestUpdate(), 200);
        setTimeout(() => this.requestUpdate(), 800);
      }

      disconnectedCallback() {
        this.list?.removeEventListener('scroll', this.onScroll);
        this.prev?.removeEventListener('click', this.onPrev);
        this.next?.removeEventListener('click', this.onNext);
        this.mql?.removeEventListener('change', this.onResize);
        this.resizeObserver?.disconnect();
      }

      get orientation() {
        return this.mql.matches ? 'vertical' : 'horizontal';
      }

      get items() {
        return Array.from(this.list?.children || []).filter((n) => n.nodeType === 1);
      }

      get step() {
        return this.mql.matches ? this.stepDesktop : this.stepMobile;
      }

      getOffset() {
        // Use cached value when available to avoid repeated layout reads
        if (this._itemOffset && Number.isFinite(this._itemOffset)) return this._itemOffset;

        const items = this.items;
        if (items.length < 2) {
          const r = items[0]?.getBoundingClientRect();
          const style = this.list ? getComputedStyle(this.list) : null;
          const gap = style
            ? parseFloat((this.orientation === 'vertical' ? style.rowGap : style.columnGap) || style.gap || '0')
            : 0;
          if (!r) return 100; // fallback
          this._itemOffset = Math.max(1, Math.round((this.orientation === 'vertical' ? r.height : r.width) + gap));
          return this._itemOffset;
        }
        const r0 = items[0].getBoundingClientRect();
        const r1 = items[1].getBoundingClientRect();
        const delta = this.orientation === 'vertical' ? r1.top - r0.top : r1.left - r0.left;
        if (Math.abs(delta) > 0) {
          this._itemOffset = Math.max(1, Math.round(Math.abs(delta)));
          return this._itemOffset;
        }
        const style = this.list ? getComputedStyle(this.list) : null;
        const gap = style
          ? parseFloat((this.orientation === 'vertical' ? style.rowGap : style.columnGap) || style.gap || '0')
          : 0;
        const size = this.orientation === 'vertical' ? r0.height : r0.width;
        this._itemOffset = Math.max(1, Math.round(size + gap));
        return this._itemOffset;
      }

      scrollPrev() {
        const amount = this.step * this.getOffset();
        if (this.orientation === 'vertical') {
          this.scrollToPosition(this.list.scrollTop - amount, 'vertical');
        } else {
          this.scrollToPosition(this.list.scrollLeft - amount, 'horizontal');
        }
      }

      scrollNext() {
        const amount = this.step * this.getOffset();
        if (this.orientation === 'vertical') {
          this.scrollToPosition(this.list.scrollTop + amount, 'vertical');
        } else {
          this.scrollToPosition(this.list.scrollLeft + amount, 'horizontal');
        }
      }

      scrollToPosition(pos, axis) {
        if (!this.list) return;
        if (axis === 'vertical') {
          const max = this.list.scrollHeight - this.list.clientHeight;
          const clamped = Math.max(0, Math.min(max, pos));
          this.list.scrollTo({ top: clamped, behavior: 'smooth' });
        } else {
          const max = this.list.scrollWidth - this.list.clientWidth;
          const clamped = Math.max(0, Math.min(max, pos));
          this.list.scrollTo({ left: clamped, behavior: 'smooth' });
        }
      }

      // rAF-throttled updater to batch reads/writes per frame
      requestUpdate() {
        if (this._ticking) return;
        this._ticking = true;
        requestAnimationFrame(() => {
          this._ticking = false;
          this.updateButtons();
        });
      }

      updateButtons() {
        if (!this.list) return;
        const tol = 1;
        if (this.orientation === 'vertical') {
          const scrollTop = this.list.scrollTop;
          const scrollHeight = this.list.scrollHeight;
          const clientHeight = this.list.clientHeight;
          const atStart = scrollTop <= tol;
          const atEnd = scrollTop >= scrollHeight - clientHeight - tol;
          this.prev?.toggleAttribute('disabled', atStart);
          this.next?.toggleAttribute('disabled', atEnd);
        } else {
          const scrollLeft = this.list.scrollLeft;
          const scrollWidth = this.list.scrollWidth;
          const clientWidth = this.list.clientWidth;
          const atStart = scrollLeft <= tol;
          const atEnd = scrollLeft >= scrollWidth - clientWidth - tol;
          this.prev?.toggleAttribute('disabled', atStart);
          this.next?.toggleAttribute('disabled', atEnd);
        }
      }
    }
  );
}
